import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';

export const GasCylinderDesignDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;
  const {
    materialType: savedMaterialType = '',
    otherMaterialType: savedOtherMaterialType = '',
    constructionType: savedConstructionType = '',
    minVolume: savedMinVolume = '',
    maxVolume: savedMaxVolume = '',
    testPressureUnit: savedTestPressureUnit = '',
    testPressure: savedTestPressure = '',
    contents: savedContents = '',
  } = value || {};

  const [materialType, setMaterialType] = useState(savedMaterialType);
  const [otherMaterialType, setOtherMaterialType] = useState(savedOtherMaterialType);
  const [constructionType, setConstructionType] = useState(savedConstructionType);
  const [testPressureUnit, setTestPressureUnit] = useState(savedTestPressureUnit);
  const [minVolume, setMinVolume] = useState(savedMinVolume);
  const [maxVolume, setMaxVolume] = useState(savedMaxVolume);
  const [testPressure, setTestPressure] = useState(savedTestPressure);
  const [contents, setContents] = useState(savedContents);

  const REF_CONSTANTS = PlantDesignRefData.gasCylinder;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.gasCylinder;

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const materialTypeOptions = mapRefDataPickList(refData[REF_CONSTANTS.materialTypeOptions]);
  const constructionTypeOptions = mapRefDataPickList(refData[REF_CONSTANTS.constructionTypeOptions]);
  const testPressureUnitOptions = mapRefDataPickList(refData[REF_CONSTANTS.testPressureUnitOptions]);
  const contentsOptions = mapRefDataPickList(refData[REF_CONSTANTS.contentsOptions]);

  const getTestPressureUnitText = () => {
    if (!testPressureUnitOptions || !testPressureUnit) return null;

    return testPressureUnitOptions.find((o) => o.value === testPressureUnit)?.text;
  };

  const validators = [
    {
      fieldName: 'materialType',
      fieldValue: materialType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'otherMaterialType',
      fieldValue: otherMaterialType,
      isValid: (v) => !isEmpty(v) || materialType !== REF_CONSTANTS.otherMaterialType,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'constructionType',
      fieldValue: constructionType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'testPressureUnit',
      fieldValue: testPressureUnit,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'minVolume',
      fieldValue: minVolume,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxVolume',
      fieldValue: maxVolume,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },

    {
      fieldName: 'testPressure',
      fieldValue: testPressure,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'contents',
      fieldValue: contents,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      materialType,
      otherMaterialType,
      constructionType,
      minVolume,
      maxVolume,
      testPressure,
      testPressureUnit,
      contents,
    };

    if (materialType !== REF_CONSTANTS.otherMaterialType) delete details.otherMaterialType;

    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    materialType,
    otherMaterialType,
    constructionType,
    minVolume,
    maxVolume,
    testPressure,
    testPressureUnit,
    contents,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <RadioOrDropdownSelector
        id="materialType"
        name="materialType"
        options={materialTypeOptions}
        title={PAGE_CONSTANTS.materialType.label}
        errorMessage={errors?.materialType?.errorMessage}
        hasError={showErrors && errors?.materialType?.invalid}
        onChange={(v) => {
          setMaterialType(v);
          setOtherMaterialType('');
        }}
        value={materialType}
        showExtraInput={materialType === REF_CONSTANTS.otherMaterialType}
        onChangeExtraInput={setOtherMaterialType}
        extraInputValue={otherMaterialType}
        extraInputErrorMessage={errors?.otherMaterialType?.errorMessage}
        extraInputHasError={showErrors && errors?.otherMaterialType?.invalid}
        extraInputTitle={PAGE_CONSTANTS.otherMaterialTypeLabel}
        extraInputType="textarea"
        margin={{ top: 'xl' }}
        maxOptionsLengthForRadio={10}
      />

      <FormRadioGroup
        id="constructionType"
        name="constructionType"
        vertical={true}
        options={constructionTypeOptions}
        onChange={setConstructionType}
        value={constructionType}
        errorMessage={errors?.constructionType?.errorMessage}
        hasError={showErrors && errors?.constructionType?.invalid}
        legend={PAGE_CONSTANTS.constructionType.label}
        margin={{ top: 'lg' }}
      />

      <FormGroup
        id="minVolume"
        label={PAGE_CONSTANTS.minVolume.label}
        isRequired
        margin={{ top: 'md' }}
        errorMessage={errors?.minVolume?.errorMessage}
        hasError={showErrors && errors?.minVolume?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.minVolume.suffix}
          onChange={(e) => {
            setMinVolume(e.target.value);
          }}
          value={minVolume}
        />
      </FormGroup>

      <FormGroup
        id="maxVolume"
        label={PAGE_CONSTANTS.maxVolume.label}
        isRequired
        margin={{ top: 'md' }}
        errorMessage={errors?.maxVolume?.errorMessage}
        hasError={showErrors && errors?.maxVolume?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxVolume.suffix}
          onChange={(e) => {
            setMaxVolume(e.target.value);
          }}
          value={maxVolume}
        />
      </FormGroup>

      <FormRadioGroup
        id="testPressureUnit"
        name="testPressureUnit"
        vertical={false}
        options={testPressureUnitOptions}
        onChange={setTestPressureUnit}
        value={testPressureUnit}
        errorMessage={errors?.testPressureUnit?.errorMessage}
        hasError={showErrors && errors?.testPressureUnit?.invalid}
        legend={PAGE_CONSTANTS.testPressure.label}
        margin={{ top: 'lg' }}
      />

      {testPressureUnit && (
        <FormGroup
          id="testPressure"
          label=""
          isRequired
          errorMessage={errors?.testPressure?.errorMessage}
          hasError={showErrors && errors?.testPressure?.invalid}
          className={styles.inputWithSuffix}
        >
          <Input
            suffix={getTestPressureUnitText()}
            onChange={(e) => {
              setTestPressure(e.target.value);
            }}
            value={testPressure}
          />
        </FormGroup>
      )}

      <FormRadioGroup
        id="contents"
        name="contents"
        vertical={true}
        options={contentsOptions}
        onChange={setContents}
        value={contents}
        errorMessage={errors?.contents?.errorMessage}
        hasError={showErrors && errors?.contents?.invalid}
        legend={PAGE_CONSTANTS.contents.label}
        margin={{ top: 'lg' }}
      />
    </>
  );
});

GasCylinderDesignDetails.displayName = 'GasCylinderDesignDetails';

GasCylinderDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
